import React, { useState, useEffect, useRef } from "react";

function escapeHtml(str: string | number | undefined | null): string {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
import {
  ShieldAlert,
  ShieldCheck,
  Scale,
  Sparkles,
  AlertTriangle,
  AlertCircle,
  Printer,
  Copy,
  Check,
  RefreshCw,
  Edit3,
  Save,
  CheckSquare,
  FileText,
} from "lucide-react";
import { Opportunity, StrategicAssessment, RiskSeverity, EffortBreakdown } from "../../types";
import {
  subscribeToLatestAssessment,
  runStrategicAssessment,
  toggleRiskAcknowledgment,
  updateExecutiveEffortOverride,
  saveAssessmentUserNotes,
} from "../../services/assessmentService";
import { ExpandableCard } from "../common/ExpandableCard";

interface AssessmentTabProps {
  opportunity: Opportunity;
  onOpenQualifyModal?: () => void;
}

export const AssessmentTab: React.FC<AssessmentTabProps> = ({
  opportunity,
  onOpenQualifyModal,
}) => {
  const [assessment, setAssessment] = useState<StrategicAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters & State
  const [severityFilter, setSeverityFilter] = useState<RiskSeverity | "all" | "acknowledged">(
    "all",
  );
  const [copiedType, setCopiedType] = useState<"markdown" | "html" | null>(null);
  const [isEditingEffort, setIsEditingEffort] = useState(false);
  const [effortDraft, setEffortDraft] = useState<EffortBreakdown>({
    mode: "manual_override",
    writingHours: 0,
    smeReviewHours: 0,
    managementHours: 0,
    totalHours: 0,
    notes: "",
  });
  const [userNotes, setUserNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSavedAlert, setNotesSavedAlert] = useState(false);

  const notesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const unsub = subscribeToLatestAssessment(
      opportunity.id,
      (data) => {
        setAssessment(data);
        if (data) {
          setUserNotes(data.userNotes || "");
          if (data.executiveBrief?.effortEstimate) {
            setEffortDraft(data.executiveBrief.effortEstimate);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load strategic assessment:", err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [opportunity.id]);

  const handleRunAssessment = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await runStrategicAssessment(opportunity.id);
      setAssessment(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run assessment");
    } finally {
      setRunning(false);
    }
  };

  const handleToggleAck = async (riskId: string, currentAck: boolean) => {
    if (!assessment) return;
    try {
      await toggleRiskAcknowledgment(opportunity.id, assessment.id, riskId, !currentAck);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update risk acknowledgment");
    }
  };

  const handleSaveEffort = async () => {
    if (!assessment) return;
    try {
      const total =
        Number(effortDraft.writingHours || 0) +
        Number(effortDraft.smeReviewHours || 0) +
        Number(effortDraft.managementHours || 0);
      const updated: EffortBreakdown = {
        ...effortDraft,
        mode: "manual_override",
        totalHours: total,
      };
      await updateExecutiveEffortOverride(opportunity.id, assessment.id, updated);
      setIsEditingEffort(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update effort estimate");
    }
  };

  const handleSaveNotes = async () => {
    if (!assessment) return;
    setIsSavingNotes(true);
    try {
      await saveAssessmentUserNotes(opportunity.id, assessment.id, userNotes);
      setNotesSavedAlert(true);
      if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
      notesTimeoutRef.current = setTimeout(() => setNotesSavedAlert(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCopyMarkdown = () => {
    if (!assessment) return;
    const brief = assessment.executiveBrief;
    const md = `# Executive Opportunity Brief: ${opportunity.title}
**Client:** ${opportunity.client} | **Due Date:** ${opportunity.dueDate}
**Overall Fit Score:** ${assessment.overallFitScore}% (${assessment.recommendation.toUpperCase()})
**Model Used:** ${assessment.modelUsed} | **Generated:** ${new Date(assessment.generatedAt).toLocaleDateString()}

## Strategic Recommendation & Rationale
${assessment.recommendationRationale}

## Client Overview & Core Pain Points
${brief.clientOverview}

### Client Pain Points
${brief.corePainPoints.map((p) => `- ${p}`).join("\n")}

## Recommended Win Theme
> **${brief.recommendedWinTheme}**

## Win Strategy Summary
${brief.winStrategySummary}

## Key Differentiators
${brief.keyDifferentiators.map((d) => `- ${d}`).join("\n")}

## Resource & Effort Estimate (${brief.effortEstimate.mode.replace("_", " ")})
- **Writing Hours:** ${brief.effortEstimate.writingHours}h
- **SME Review Hours:** ${brief.effortEstimate.smeReviewHours}h
- **Management / PM Hours:** ${brief.effortEstimate.managementHours}h
- **Total Estimated Effort:** ${brief.effortEstimate.totalHours}h
${brief.effortEstimate.notes ? `*Notes:* ${brief.effortEstimate.notes}` : ""}

## Contract & Risk Summary
${brief.topRisksSummary}

### Red Flags Identified (${assessment.redFlags.length})
${assessment.redFlags
  .map(
    (r) =>
      `### [${r.severity.toUpperCase()}] ${r.title} (${r.category})
- **Verbatim Quote:** "${r.clauseQuote}" ${r.sourcePage ? `(Page ${r.sourcePage})` : ""}
- **Recommended Redline / Mitigation:** ${r.mitigation}
- **Status:** ${r.acknowledged ? `Acknowledged by ${r.acknowledgedBy}` : "Pending Review"}`,
  )
  .join("\n\n")}

---
*Generated by MLC Proposal HQ on ${new Date(assessment.generatedAt).toLocaleString()}*
*Legal Disclaimer: ${assessment.redFlags[0]?.legalDisclaimer || "Not legal advice — route recommended redline language to legal counsel."}*
`;

    navigator.clipboard.writeText(md);
    setCopiedType("markdown");
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setCopiedType(null), 2500);
  };

  const handleCopyHtml = () => {
    if (!assessment) return;
    const brief = assessment.executiveBrief;
    const html = `
<div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1e293b;">
  <h1 style="color: #0f172a; margin-bottom: 4px;">Executive Opportunity Brief: ${escapeHtml(opportunity.title)}</h1>
  <p style="color: #64748b; font-size: 14px; margin-top: 0;">
    <strong>Client:</strong> ${escapeHtml(opportunity.client)} &nbsp;|&nbsp; 
    <strong>Due Date:</strong> ${escapeHtml(opportunity.dueDate)} &nbsp;|&nbsp; 
    <strong>Fit Score:</strong> <span style="color: #0d9488; font-weight: bold;">${assessment.overallFitScore}% (${escapeHtml(assessment.recommendation.toUpperCase())})</span>
  </p>
  <hr style="border: none; border-top: 1px solid #cbd5e1; margin: 16px 0;" />
  <h2 style="color: #0f172a;">Strategic Recommendation &amp; Rationale</h2>
  <p>${escapeHtml(assessment.recommendationRationale)}</p>
  
  <h2 style="color: #0f172a;">Client Overview &amp; Pain Points</h2>
  <p>${escapeHtml(brief.clientOverview)}</p>
  <ul>
    ${brief.corePainPoints.map((p) => `<li>${escapeHtml(p)}</li>`).join("")}
  </ul>

  <h2 style="color: #0f172a;">Recommended Win Theme</h2>
  <blockquote style="border-left: 4px solid #2ba5b5; padding-left: 12px; margin: 12px 0; color: #0f766e; font-style: italic;">
    ${escapeHtml(brief.recommendedWinTheme)}
  </blockquote>

  <h2 style="color: #0f172a;">Win Strategy Summary</h2>
  <p>${escapeHtml(brief.winStrategySummary)}</p>

  <h2 style="color: #0f172a;">Resource &amp; Effort Estimate</h2>
  <p>
    <strong>Writing:</strong> ${brief.effortEstimate.writingHours}h &nbsp;|&nbsp;
    <strong>SME Review:</strong> ${brief.effortEstimate.smeReviewHours}h &nbsp;|&nbsp;
    <strong>PM:</strong> ${brief.effortEstimate.managementHours}h &nbsp;|&nbsp;
    <strong>Total:</strong> <span style="font-weight: bold; color: #0f172a;">${brief.effortEstimate.totalHours}h</span>
  </p>

  <h2 style="color: #0f172a;">Contract Risks &amp; Redlines (${assessment.redFlags.length})</h2>
  ${assessment.redFlags
    .map(
      (r) => `
    <div style="margin-bottom: 12px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 6px;">
      <h3 style="margin: 0 0 6px 0; font-size: 15px; color: #b91c1c;">[${escapeHtml(r.severity.toUpperCase())}] ${escapeHtml(r.title)}</h3>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Verbatim Clause:</strong> <em>"${escapeHtml(r.clauseQuote)}"</em></p>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Proposed Redline / Mitigation:</strong> ${escapeHtml(r.mitigation)}</p>
      <p style="margin: 4px 0; font-size: 12px; color: #64748b;">Status: ${r.acknowledged ? `Acknowledged by ${escapeHtml(r.acknowledgedBy)}` : "Pending Review"}</p>
    </div>`,
    )
    .join("")}
  <p style="font-size: 11px; color: #94a3b8; margin-top: 16px;"><em>Legal Disclaimer: Not legal advice — route recommended redline language to legal counsel.</em></p>
</div>`;

    const blobHtml = new Blob([html], { type: "text/html" });
    const blobText = new Blob([brief.winStrategySummary], { type: "text/plain" });
    const clipboardItem = new ClipboardItem({
      "text/html": blobHtml,
      "text/plain": blobText,
    });
    navigator.clipboard.write([clipboardItem]);
    setCopiedType("html");
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setCopiedType(null), 2500);
  };

  const handlePrintBrief = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="text-center space-y-2">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#2ba5b5] border-r-transparent" />
          <p className="text-xs text-slate-500">Loading strategic assessment...</p>
        </div>
      </div>
    );
  }

  // Empty state when no assessment has been run yet
  if (!assessment) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/40">
          <Scale className="mx-auto h-12 w-12 text-teal-600 dark:text-teal-400" />
          <h3 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
            No Strategic Assessment Generated Yet
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-xs leading-relaxed text-slate-500">
            Generate an AI-powered 4-pillar fit score, deep contract risk scanner (identifying
            unlimited liability, onerous SLAs, IP traps, wired specs), and a 1-page printable
            Executive Opportunity Brief tailored to Multilingual Connections.
          </p>

          {error && (
            <div className="mx-auto mt-4 max-w-md rounded-lg bg-red-50 p-2.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleRunAssessment}
              disabled={running}
              className="btn-animate inline-flex items-center gap-2 rounded-lg bg-[#2ba5b5] px-5 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-[#238a97] disabled:opacity-50"
            >
              {running ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Auditing Contract & Evaluating Fit...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span>Run Strategic Assessment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filtered red flags
  const redFlags = assessment.redFlags || [];
  const filteredFlags = redFlags.filter((f) => {
    if (severityFilter === "all") return true;
    if (severityFilter === "acknowledged") return Boolean(f.acknowledged);
    return f.severity === severityFilter;
  });

  const criticalCount = redFlags.filter((f) => f.severity === "critical").length;
  const highCount = redFlags.filter((f) => f.severity === "high").length;
  const acknowledgedCount = redFlags.filter((f) => f.acknowledged).length;

  const score = assessment.overallFitScore;
  const recommendation = assessment.recommendation;

  const recommendationColor =
    recommendation === "go"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300"
      : recommendation === "caution"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300"
        : "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300";

  const recommendationLabel =
    recommendation === "go"
      ? "Strong Fit (Go)"
      : recommendation === "caution"
        ? "Proceed with Caution"
        : "Low Fit (No-Go)";

  const brief = assessment.executiveBrief;

  return (
    <div className="space-y-6">
      {/* 1. Top Executive Score & Recommendation Banner */}
      <div className="glass rounded-xl border border-slate-200/80 bg-white/80 p-5 shadow-sm dark:border-slate-800/80 dark:bg-slate-900/80 print:hidden">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            {/* Score Ring / Badge */}
            <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl border-2 border-teal-500/30 bg-teal-50 shadow-inner dark:bg-teal-950/40">
              <span className="text-2xl font-black text-slate-900 dark:text-white">{score}%</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-700 dark:text-teal-300">
                Fit Score
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-xs font-bold uppercase tracking-wider ${recommendationColor}`}
                >
                  {recommendation === "go" ? (
                    <ShieldCheck className="h-3.5 w-3.5" />
                  ) : recommendation === "caution" ? (
                    <AlertTriangle className="h-3.5 w-3.5" />
                  ) : (
                    <ShieldAlert className="h-3.5 w-3.5" />
                  )}
                  {recommendationLabel}
                </span>

                <span className="text-xs text-slate-400">
                  Model: {assessment.modelUsed} • Assessed:{" "}
                  {new Date(assessment.generatedAt).toLocaleDateString()}
                </span>
              </div>

              <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                {assessment.recommendationRationale}
              </p>

              {/* Status Pills */}
              <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500">
                <span>
                  <strong>{assessment.baseRequirementCount}</strong> RFP Requirements Analyzed
                </span>
                <span>•</span>
                <span
                  className={
                    criticalCount > 0 ? "font-semibold text-rose-600 dark:text-rose-400" : ""
                  }
                >
                  <strong>{criticalCount}</strong> Critical Legal/Contract Risks
                </span>
                <span>•</span>
                <span>
                  <strong>{acknowledgedCount}</strong> of <strong>{redFlags.length}</strong> Risks
                  Acknowledged
                </span>
              </div>
            </div>
          </div>

          {/* Action Bridge Buttons */}
          <div className="flex flex-wrap items-center gap-2 md:flex-col md:items-end">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRunAssessment}
                disabled={running}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${running ? "animate-spin" : ""}`} />
                <span>{running ? "Re-assessing..." : "Re-run Assessment"}</span>
              </button>

              {onOpenQualifyModal && (
                <button
                  type="button"
                  onClick={onOpenQualifyModal}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97]"
                >
                  <CheckSquare className="h-4 w-4" />
                  <span>Feed into Qualify Decision</span>
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handlePrintBrief}
                className="btn-animate inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                <Printer className="h-3 w-3" />
                <span>Print Brief</span>
              </button>
              <button
                type="button"
                onClick={handleCopyHtml}
                className="btn-animate inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                {copiedType === "html" ? (
                  <Check className="h-3 w-3 text-teal-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                <span>Copy for Docs</span>
              </button>
              <button
                type="button"
                onClick={handleCopyMarkdown}
                className="btn-animate inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                {copiedType === "markdown" ? (
                  <Check className="h-3 w-3 text-teal-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                <span>Markdown</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Four Fit Pillars (Probability to Win Breakdown) */}
      <div className="space-y-3 print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-[#2ba5b5]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              Probability to Win: 4-Pillar Breakdown
            </h2>
          </div>
          <span className="text-xs text-slate-400">
            Click any pillar card to view evidence quotes
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {assessment.pillars.map((pillar) => {
            const pillarStatusColor =
              pillar.status === "strong"
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                : pillar.status === "moderate"
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300";

            return (
              <ExpandableCard
                key={pillar.pillar}
                title={pillar.label}
                badge={`${pillar.score}% • ${pillar.status.toUpperCase()}`}
                badgeColor={pillarStatusColor}
                defaultExpanded={false}
                summary={
                  <div className="space-y-1">
                    <p className="text-xs text-slate-600 dark:text-slate-300">{pillar.summary}</p>
                    <div className="flex items-center gap-2 text-[11px] text-slate-400">
                      <span>Weight: {Math.round(pillar.weight * 100)}%</span>
                      <span>•</span>
                      <span>{pillar.evidenceQuotes?.length || 0} Evidence Quotes</span>
                    </div>
                  </div>
                }
                details={
                  <div className="space-y-3 text-xs">
                    {/* Strengths */}
                    {pillar.strengths.length > 0 && (
                      <div>
                        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                          Strengths:
                        </span>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-slate-600 dark:text-slate-300">
                          {pillar.strengths.map((str, i) => (
                            <li key={i}>{str}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Gaps / Watch Items */}
                    {pillar.gaps.length > 0 && (
                      <div>
                        <span className="font-semibold text-amber-700 dark:text-amber-400">
                          Gaps &amp; Considerations:
                        </span>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-slate-600 dark:text-slate-300">
                          {pillar.gaps.map((gap, i) => (
                            <li key={i}>{gap}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Evidence Quotes */}
                    {pillar.evidenceQuotes && pillar.evidenceQuotes.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <span className="font-semibold text-slate-700 dark:text-slate-300">
                          Verbatim Evidence from RFP:
                        </span>
                        <div className="space-y-1.5">
                          {pillar.evidenceQuotes.map((q, i) => (
                            <blockquote
                              key={i}
                              className="rounded-md border-l-2 border-teal-500 bg-slate-50 p-2 italic text-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
                            >
                              &ldquo;{q.quote}&rdquo;
                              {q.sourcePage && (
                                <span className="ml-1.5 font-normal not-italic text-slate-400">
                                  (Page {q.sourcePage})
                                </span>
                              )}
                            </blockquote>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                }
              />
            );
          })}
        </div>
      </div>

      {/* 3. Red-Flag & Contract Risk Scanner */}
      <div className="space-y-3 print:hidden">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-rose-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              Contract Risk &amp; Red-Flag Scanner ({redFlags.length})
            </h2>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5">
            {(["all", "critical", "high", "medium", "acknowledged"] as const).map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setSeverityFilter(filter)}
                className={`rounded-md px-2 py-0.5 text-xs font-semibold capitalize transition-colors ${
                  severityFilter === filter
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                {filter === "all"
                  ? `All (${redFlags.length})`
                  : filter === "acknowledged"
                    ? `Acknowledged (${acknowledgedCount})`
                    : filter === "critical"
                      ? `Critical (${criticalCount})`
                      : filter === "high"
                        ? `High (${highCount})`
                        : filter}
              </button>
            ))}
          </div>
        </div>

        {/* Legal Disclaimer Callout */}
        <div className="flex items-center gap-2 rounded-lg border border-amber-200/80 bg-amber-50/70 p-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>
            <strong>Legal Risk Notice:</strong>{" "}
            {redFlags[0]?.legalDisclaimer ||
              "Not legal advice — route recommended redline language to legal counsel."}
          </span>
        </div>

        {filteredFlags.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500 dark:border-slate-800">
            No risks matching filter &ldquo;{severityFilter}&rdquo;.
          </div>
        ) : (
          <div className="space-y-2.5">
            {filteredFlags.map((risk) => {
              const severityColor =
                risk.severity === "critical"
                  ? "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300"
                  : risk.severity === "high"
                    ? "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300"
                    : risk.severity === "medium"
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/60 dark:text-yellow-300"
                      : "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300";

              return (
                <ExpandableCard
                  key={risk.id}
                  title={risk.title}
                  badge={`${risk.severity.toUpperCase()} • ${risk.category.replace("_", " ").toUpperCase()}`}
                  badgeColor={severityColor}
                  defaultExpanded={false}
                  headerActions={
                    <label
                      onClick={(e) => e.stopPropagation()}
                      className="flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(risk.acknowledged)}
                        onChange={() => handleToggleAck(risk.id, Boolean(risk.acknowledged))}
                        className="h-3.5 w-3.5 rounded text-[#2ba5b5] focus:ring-0"
                      />
                      <span>{risk.acknowledged ? "Acknowledged" : "Acknowledge"}</span>
                    </label>
                  }
                  summary={
                    <div className="space-y-1">
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        {risk.description}
                      </p>
                      {risk.acknowledged && (
                        <p className="text-[11px] text-teal-600 dark:text-teal-400">
                          ✓ Acknowledged by {risk.acknowledgedBy || "Team"}{" "}
                          {risk.acknowledgedAt
                            ? `on ${new Date(risk.acknowledgedAt).toLocaleDateString()}`
                            : ""}
                        </p>
                      )}
                    </div>
                  }
                  details={
                    <div className="space-y-3 text-xs">
                      {/* Exact Verbatim Quote */}
                      <div>
                        <span className="font-semibold text-rose-700 dark:text-rose-400">
                          Verbatim Clause from RFP / Contract:
                        </span>
                        <blockquote className="mt-1 rounded-md border-l-2 border-rose-500 bg-rose-50/50 p-2.5 font-mono text-[11px] leading-relaxed text-slate-800 dark:bg-rose-950/30 dark:text-slate-200">
                          &ldquo;{risk.clauseQuote}&rdquo;
                          {risk.sourcePage && (
                            <span className="ml-1.5 not-italic text-slate-400 font-sans">
                              (Page {risk.sourcePage})
                            </span>
                          )}
                        </blockquote>
                      </div>

                      {/* Mitigation / Lawyer-style counter-clause */}
                      <div>
                        <span className="font-semibold text-teal-700 dark:text-teal-400">
                          Recommended Counter-Clause / Redline:
                        </span>
                        <div className="mt-1 rounded-md border border-teal-200/80 bg-teal-50/50 p-2.5 leading-relaxed text-slate-800 dark:border-teal-900/40 dark:bg-teal-950/20 dark:text-slate-200">
                          {risk.mitigation}
                        </div>
                      </div>
                    </div>
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* 4. 1-Page Executive Opportunity Brief (Screen & Print Layout) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#2ba5b5]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              1-Page Executive Opportunity Brief
            </h2>
          </div>
          <span className="text-xs text-slate-400">Ready for leadership review &amp; print</span>
        </div>

        {/* Printable Card */}
        <div className="glass rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 print:border-none print:shadow-none print:p-0">
          {/* Header on print */}
          <div className="border-b border-slate-200 pb-4 dark:border-slate-800 print:border-b-2 print:border-slate-800">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#2ba5b5]">
                  {opportunity.client}
                </span>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white print:text-2xl">
                  {opportunity.title}
                </h1>
                <p className="mt-0.5 text-xs text-slate-500">
                  Due: {opportunity.dueDate} • Questions Due:{" "}
                  {opportunity.questionsDueDate || "None"}
                </p>
              </div>

              <div className="text-right">
                <span
                  className={`inline-block rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${recommendationColor}`}
                >
                  {score}% {recommendationLabel}
                </span>
                <div className="mt-1 text-[11px] text-slate-400">MLC Proposal HQ Brief</div>
              </div>
            </div>
          </div>

          {/* Grid content */}
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 print:grid-cols-2">
            {/* Left Column: Context, Themes & Strategy */}
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Client Background &amp; Procurement Context
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                  {brief.clientOverview}
                </p>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Core Client Pain Points
                </h3>
                <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-700 dark:text-slate-300">
                  {brief.corePainPoints.map((pt, i) => (
                    <li key={i}>{pt}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700 dark:text-teal-400">
                  Recommended Win Theme
                </h3>
                <blockquote className="mt-1 rounded-md border-l-3 border-[#2ba5b5] bg-teal-50/50 p-2.5 text-xs font-medium italic text-slate-800 dark:bg-teal-950/20 dark:text-slate-200">
                  &ldquo;{brief.recommendedWinTheme}&rdquo;
                </blockquote>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Win Strategy Positioning
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                  {brief.winStrategySummary}
                </p>
              </div>
            </div>

            {/* Right Column: Differentiators, Effort & Risks */}
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Key MLC Differentiators
                </h3>
                <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-700 dark:text-slate-300">
                  {brief.keyDifferentiators.map((diff, i) => (
                    <li key={i}>{diff}</li>
                  ))}
                </ul>
              </div>

              {/* Flexible Effort Breakdown */}
              <div className="rounded-lg border border-slate-200/80 bg-slate-50/70 p-3 dark:border-slate-800 dark:bg-slate-800/40">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Estimated Proposal Effort ({brief.effortEstimate.mode.replace("_", " ")})
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsEditingEffort((prev) => !prev)}
                    className="btn-animate inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ba5b5] hover:underline print:hidden"
                  >
                    <Edit3 className="h-3 w-3" />
                    <span>{isEditingEffort ? "Cancel" : "Override Hours"}</span>
                  </button>
                </div>

                {isEditingEffort ? (
                  <div className="mt-3 space-y-2 print:hidden">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">
                          Writing (h)
                        </label>
                        <input
                          type="number"
                          value={effortDraft.writingHours}
                          onChange={(e) =>
                            setEffortDraft((prev) => ({
                              ...prev,
                              writingHours: Number(e.target.value),
                            }))
                          }
                          className="mt-0.5 w-full rounded border border-slate-300 p-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">
                          SME Review (h)
                        </label>
                        <input
                          type="number"
                          value={effortDraft.smeReviewHours}
                          onChange={(e) =>
                            setEffortDraft((prev) => ({
                              ...prev,
                              smeReviewHours: Number(e.target.value),
                            }))
                          }
                          className="mt-0.5 w-full rounded border border-slate-300 p-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">
                          Management (h)
                        </label>
                        <input
                          type="number"
                          value={effortDraft.managementHours}
                          onChange={(e) =>
                            setEffortDraft((prev) => ({
                              ...prev,
                              managementHours: Number(e.target.value),
                            }))
                          }
                          className="mt-0.5 w-full rounded border border-slate-300 p-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold text-slate-500">
                        Effort Notes
                      </label>
                      <input
                        type="text"
                        value={effortDraft.notes || ""}
                        onChange={(e) =>
                          setEffortDraft((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        placeholder="e.g. Requires technical SME interview"
                        className="mt-0.5 w-full rounded border border-slate-300 p-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                      />
                    </div>

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleSaveEffort}
                        className="btn-animate inline-flex items-center gap-1 rounded bg-[#2ba5b5] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#238a97]"
                      >
                        <Save className="h-3 w-3" />
                        <span>Save Hours Override</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-1.5 text-xs text-slate-700 dark:text-slate-300">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="rounded bg-white p-1.5 shadow-2xs dark:bg-slate-900">
                        <span className="block text-[10px] text-slate-400">Writing</span>
                        <span className="font-bold">{brief.effortEstimate.writingHours}h</span>
                      </div>
                      <div className="rounded bg-white p-1.5 shadow-2xs dark:bg-slate-900">
                        <span className="block text-[10px] text-slate-400">SME Review</span>
                        <span className="font-bold">{brief.effortEstimate.smeReviewHours}h</span>
                      </div>
                      <div className="rounded bg-white p-1.5 shadow-2xs dark:bg-slate-900">
                        <span className="block text-[10px] text-slate-400">PM</span>
                        <span className="font-bold">{brief.effortEstimate.managementHours}h</span>
                      </div>
                      <div className="rounded bg-teal-50 p-1.5 shadow-2xs dark:bg-teal-950/40">
                        <span className="block text-[10px] text-teal-700 dark:text-teal-300">
                          Total
                        </span>
                        {brief.effortEstimate.totalHours === 0 ? (
                          <span className="font-bold text-amber-600 dark:text-amber-400 text-[11px]">
                            Needs Team Input
                          </span>
                        ) : (
                          <span className="font-extrabold text-[#2ba5b5]">
                            {brief.effortEstimate.totalHours}h
                          </span>
                        )}
                      </div>
                    </div>
                    {brief.effortEstimate.notes && (
                      <p className="pt-1 text-[11px] italic text-slate-500">
                        Note: {brief.effortEstimate.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Key Contract & Operational Risks */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Top Contract &amp; Qualification Risks
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                  {brief.topRisksSummary}
                </p>
              </div>

              {/* Human Strategist Notes Editor */}
              <div className="space-y-1.5 print:hidden">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Proposal Strategist Team Notes
                  </h3>
                  {notesSavedAlert && (
                    <span className="text-[11px] font-semibold text-teal-600">✓ Notes Saved</span>
                  )}
                </div>
                <textarea
                  rows={2}
                  value={userNotes}
                  onChange={(e) => setUserNotes(e.target.value)}
                  placeholder="Record strategic team takeaways, agreed negotiation redlines, or qualification caveats..."
                  className="w-full rounded-lg border border-slate-300 p-2 text-xs leading-relaxed text-slate-900 focus:border-[#2ba5b5] focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveNotes}
                    disabled={isSavingNotes}
                    className="btn-animate inline-flex items-center gap-1 rounded bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <Save className="h-3 w-3" />
                    <span>{isSavingNotes ? "Saving..." : "Save Notes"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer note on print */}
          <div className="mt-6 border-t border-slate-100 pt-3 text-[10px] text-slate-400 dark:border-slate-800">
            MLC Proposal HQ • Generated on {new Date(assessment.generatedAt).toLocaleString()} •
            Legal Disclaimer:{" "}
            {assessment.redFlags[0]?.legalDisclaimer ||
              "Not legal advice — route recommended redline language to legal counsel."}
          </div>
        </div>
      </div>
    </div>
  );
};
